<template>
  <Dialog
    :visible="visible"
    modal
    :header="title"
    class="procedures-selector-dialog"
    @update:visible="emit('update:visible', $event)"
  >
    <div class="procedures-selector">
      <div class="procedures-selector__search">
        <InputText
          v-model.trim="searchText"
          :placeholder="t('wkt.client.procedures.selector.search')"
        />
      </div>

      <div
        v-if="!filteredDefinitions.length"
        class="procedures-selector__empty"
      >
        {{ t("wkt.client.procedures.selector.empty_search") }}
      </div>

      <button
        v-for="definition in filteredDefinitions"
        :key="definition.id"
        type="button"
        class="procedures-selector__item"
        @click="onSelect(definition.id)"
      >
        <span class="procedures-selector__icon">
          <i :class="definitionIconClass(definition)" aria-hidden="true"></i>
        </span>
        <span class="procedures-selector__copy">
          <strong>{{ definition.name }}</strong>
          <span v-if="definition.description">{{ definition.description }}</span>
        </span>
      </button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { FullProcedureDefinitionDTO } from "@wkt/api/procedures";

const props = defineProps<{
  visible: boolean;
  title: string;
  definitions: FullProcedureDefinitionDTO[];
}>();

const { t } = useI18n();
const emit = defineEmits<{
  "update:visible": [value: boolean];
  select: [definitionId: number];
}>();

const searchText = ref("");

const filteredDefinitions = computed(() => {
  const query = normalizeSearchText(searchText.value);
  if (!query) {
    return props.definitions;
  }

  return props.definitions.filter((definition) => {
    const haystack = normalizeSearchText([
      definition.name,
      definition.description,
      definition.freeTextLabel,
      definition.quantityLabel,
      definition.projectLabel,
      definition.activityTypeLabel,
      definition.procedureClass,
      definition.externalId,
    ]
      .filter(Boolean)
      .join(" "));

    return haystack.includes(query);
  });
});

watch(
  () => props.visible,
  (visible) => {
    if (!visible) {
      searchText.value = "";
    }
  }
);

function definitionIconClass(definition: FullProcedureDefinitionDTO): string {
  const iconType = definition.iconType || "pi pi-file";
  return iconType.startsWith("pi ") ? iconType : "pi pi-file";
}

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function onSelect(definitionId?: number | null): void {
  if (definitionId == null) {
    return;
  }

  emit("update:visible", false);
  emit("select", definitionId);
}
</script>

<style scoped>
.procedures-selector {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.procedures-selector__search :deep(.p-inputtext) {
  width: 100%;
}

.procedures-selector__empty {
  padding: 1rem;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #f8fbff;
  color: #607284;
}

.procedures-selector__item {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  width: 100%;
  border: 1px solid #d9e2ec;
  background: #fff;
  border-radius: 16px;
  padding: 0.95rem 1rem;
  cursor: pointer;
  text-align: left;
}

.procedures-selector__item:hover {
  border-color: rgba(var(--primary-rgb), 0.4);
  background: #f8fbff;
}

.procedures-selector__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary-color);
  flex-shrink: 0;
}

.procedures-selector__copy {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.procedures-selector__copy strong {
  color: #12263a;
}

.procedures-selector__copy span {
  color: #607284;
  font-size: 0.9rem;
}
</style>
